// SEO and AEO utilities for construction cost estimation
import { US_STATES, getCitiesForState } from './statesAndCities';

// Construction project types with SEO-friendly variations
export const PROJECT_TYPES = {
  'single-family': {
    label: 'Single Family Home',
    seoVariations: [
      'single family home',
      'single family house',
      'detached home',
      'residential home',
      'family house',
      'custom home',
      'new home construction'
    ],
    avgCostRange: '$200-500 per sq ft'
  },
  'multi-family': {
    label: 'Multi-Family Home',
    seoVariations: [
      'multi family home',
      'duplex',
      'triplex',
      'apartment building',
      'multi unit building',
      'rental property',
      'investment property'
    ],
    avgCostRange: '$150-350 per sq ft'
  },
  'home-improvements': {
    label: 'Home Improvements',
    seoVariations: [
      'home renovation',
      'home remodel',
      'house renovation',
      'home improvement',
      'home addition',
      'kitchen remodel',
      'bathroom renovation',
      'home extension'
    ],
    avgCostRange: '$100-300 per sq ft'
  },
  'adu': {
    label: 'Accessory Dwelling Unit (ADU)',
    seoVariations: [
      'adu',
      'accessory dwelling unit',
      'in-law unit',
      'granny flat',
      'secondary dwelling',
      'backyard cottage',
      'detached ad unit'
    ],
    avgCostRange: '$150-400 per sq ft'
  },
  'kitchen-upgrade': {
    label: 'Kitchen Upgrade',
    seoVariations: [
      'kitchen remodel',
      'kitchen renovation',
      'kitchen upgrade',
      'kitchen renovation cost',
      'kitchen remodeling',
      'kitchen makeover',
      'kitchen renovation project'
    ],
    avgCostRange: '$100-400 per sq ft'
  },
  'bathroom-upgrade': {
    label: 'Bathroom Upgrade',
    seoVariations: [
      'bathroom remodel',
      'bathroom renovation',
      'bathroom upgrade',
      'bathroom renovation cost',
      'bathroom remodeling',
      'bathroom makeover',
      'bathroom renovation project'
    ],
    avgCostRange: '$150-500 per sq ft'
  }
};

// Generate SEO-optimized page metadata with 2025 enhancements
export const generateSEOMetadata = (projectType, city, state) => {
  const project = PROJECT_TYPES[projectType];
  if (!project) return null;

  const year = new Date().getFullYear();
  const primaryKeyword = `${project.label.toLowerCase()} construction cost`;
  const locationKeyword = city && state ? `${city}, ${state}` : state || '';
  
  return {
    title: `${project.label} Construction Cost in ${locationKeyword} | ${year} Calculator & Pricing Guide`,
    description: `Calculate ${project.label.toLowerCase()} construction costs in ${locationKeyword} for ${year}. Get accurate estimates (${project.avgCostRange}) with our AI-powered cost calculator. Free instant quotes with current market data.`,
    keywords: [
      `${primaryKeyword} ${locationKeyword} ${year}`,
      `${project.label.toLowerCase()} cost calculator ${year}`,
      `construction cost estimator ${locationKeyword}`,
      `building cost ${locationKeyword} ${year}`,
      ...project.seoVariations.map(variation => `${variation} cost ${locationKeyword}`),
      `${locationKeyword} construction prices ${year}`,
      `how much does ${project.label.toLowerCase()} cost in ${year}`,
      `${project.label.toLowerCase()} price per square foot ${locationKeyword}`,
      `${year} construction rates ${locationKeyword}`,
      `current ${project.label.toLowerCase()} pricing`,
      `${project.label.toLowerCase()} cost trends ${year}`
    ].join(', '),
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": `Construction Cost Calculator ${year}`,
      "alternateName": `${project.label} Cost Estimator`,
      "description": `AI-powered ${year} construction cost estimator for ${project.label.toLowerCase()} projects in ${locationKeyword}`,
      "url": typeof window !== 'undefined' ? window.location.href : '',
      "applicationCategory": "FinanceApplication",
      "operatingSystem": "Web",
      "browserRequirements": "JavaScript",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD",
        "description": `Free construction cost estimates with ${year} market data`,
        "availability": "https://schema.org/InStock"
      },
      "areaServed": {
        "@type": "State",
        "name": state
      },
      "serviceType": "Construction Cost Estimation",
      "dateModified": new Date().toISOString(),
      "softwareVersion": `${year}.1`,
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "ratingCount": "75000"
      }
    }
  };
};

// Generate FAQ schema for specific combinations
export const generateFAQSchema = (projectType, city, state) => {
  const project = PROJECT_TYPES[projectType];
  const location = city && state ? `${city}, ${state}` : state;
  
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `How much does it cost to build a ${project.label.toLowerCase()} in ${location}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `The cost to build a ${project.label.toLowerCase()} in ${location} typically ranges from ${project.avgCostRange} depending on size, materials, and finishes. Use our AI-powered calculator for accurate estimates based on your specific requirements.`
        }
      },
      {
        "@type": "Question",
        "name": `What factors affect ${project.label.toLowerCase()} construction costs in ${location}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key factors include square footage, materials quality, labor costs, site conditions, permits, and local building codes. Our calculator considers all these factors for accurate estimates."
        }
      },
      {
        "@type": "Question",
        "name": `How accurate are construction cost estimates for ${location}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our AI-powered estimates are typically accurate within 10-15% for conceptual planning. For detailed estimates, we recommend consulting with local contractors."
        }
      }
    ]
  };
};

// Generate location-based landing page content
export const generateLocationContent = (projectType, city, state) => {
  const project = PROJECT_TYPES[projectType];
  const location = city && state ? `${city}, ${state}` : state;
  
  return {
    h1: `${project.label} Construction Cost Calculator - ${location}`,
    h2: `Calculate ${project.label} Costs in ${location}`,
    intro: `Get accurate ${project.label.toLowerCase()} construction cost estimates for ${location}. Our AI-powered calculator provides instant quotes based on local market rates, materials, and labor costs.`,
    benefits: [
      `Accurate ${location} market pricing`,
      'AI-powered cost analysis',
      'Instant detailed estimates',
      'Local building code considerations',
      'Material and labor cost breakdowns'
    ]
  };
};

// Generate all possible SEO keyword combinations
export const generateAllSEOCombinations = () => {
  const combinations = [];
  
  Object.entries(PROJECT_TYPES).forEach(([typeId, typeData]) => {
    US_STATES.forEach(state => {
      const cities = getCitiesForState(state);
      
      const year = new Date().getFullYear();
      
      // State-level combinations
      typeData.seoVariations.forEach(variation => {
        combinations.push({
          keyword: `${variation} cost ${state}`,
          url: `/estimate/${typeId}/${state.toLowerCase().replace(/\s+/g, '-')}`,
          title: `${typeData.label} Construction Cost in ${state} | ${year} Calculator`,
          priority: 0.8
        });
      });
      
      // City-level combinations (top 5 cities per state for performance)
      cities.slice(0, 5).forEach(city => {
        typeData.seoVariations.forEach(variation => {
          combinations.push({
            keyword: `${variation} cost ${city} ${state}`,
            url: `/estimate/${typeId}/${state.toLowerCase().replace(/\s+/g, '-')}/${city.toLowerCase().replace(/\s+/g, '-')}`,
            title: `${typeData.label} Construction Cost in ${city}, ${state} | ${year}`,
            priority: 1.0
          });
        });
      });
    });
  });
  
  return combinations;
};

// Generate sitemap entries for all combinations
export const generateSitemapEntries = () => {
  const baseUrl = 'https://costtoconstruct.com';
  const combinations = generateAllSEOCombinations();
  
  return combinations.map(combo => ({
    url: `${baseUrl}${combo.url}`,
    lastmod: new Date().toISOString().split('T')[0],
    changefreq: 'monthly',
    priority: combo.priority
  }));
};

// Generate robots.txt content
export const generateRobotsTxt = () => {
  return `User-agent: *
Allow: /
Allow: /estimate/*

Sitemap: https://costtoconstruct.com/sitemap.xml

# Optimize for construction cost searches
User-agent: ChatGPT-User
Allow: /
Allow: /api/

User-agent: Claude-Web
Allow: /
Allow: /api/

User-agent: Bard
Allow: /
Allow: /api/`;
};

